import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { statusColorMap } from '../util/statusColors';
import { PickUpPoint } from '../types';
import FullscreenLoader from '../components/FullScreenLoader';
import AddPickUpPointModal from '../modals/AddPickUpPointModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import BulkExclusionModal from '../modals/BulkExclusionModal';

type PickUpPointOrder = {
  id: number;
  order_reference: string;
  shelf_compartment_code: string | null;
  days_waiting_for_collection: number | null;
  overstaying_at_pick_up_point: boolean;
  status: string;
  pre_ready_for_collection: boolean;
  excluded_from_bulk_update: boolean;
  bulk_update_exclusion_reason: string | null;
  bulk_update_excluded_by: string | null;
  bulk_update_excluded_at: string | null;
  customer_id: number;
  customer: {
    full_name: string;
  };
};

type CompartmentsSummary = {
  total: number;
  occupied: number;
  available: number;
};

const formatExclusionDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const PickUpPointDetails: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickUpPoint, setPickUpPoint] = useState<PickUpPoint | null>(null);
  const [orders, setOrders] = useState<PickUpPointOrder[]>([]);
  const [compartmentsSummary, setCompartmentsSummary] = useState<CompartmentsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'ready_for_collection' | 'not_ready_for_collection'>('all');
  const [overstayingOnly, setOverstayingOnly] = useState(false);
  const [overstayDays, setOverstayDays] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [excludedCount, setExcludedCount] = useState(0);
  const [excludeModalOrder, setExcludeModalOrder] = useState<PickUpPointOrder | null>(null);
  const [includeConfirmOrder, setIncludeConfirmOrder] = useState<PickUpPointOrder | null>(null);
  const [savingExclusionOrderId, setSavingExclusionOrderId] = useState<number | null>(null);

  const fetchData = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const overstayingParam = overstayingOnly ? '&overstaying=true' : '';
      const res = await fetchWithAuth(`/pick_up_points/${id}?page=${page}${statusParam}${overstayingParam}`);
      const data = await res.json();

      setPickUpPoint(data.pick_up_point);
      setOrders(data.orders ?? []);
      setCompartmentsSummary(data.compartments_summary);
      setOverstayDays(data.collection_overstay_days ?? null);
      setCurrentPage(data.current_page);
      setTotalPages(data.total_pages);
      setTotalCount(data.total_count);
      setExcludedCount(data.excluded_from_bulk_update_count ?? 0);
    } catch (err) {
      console.error('Failed to fetch pick-up point', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const page = isNaN(initialPage) || initialPage < 1 ? 1 : initialPage;
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, statusFilter, overstayingOnly]);

  const handleGenerateCollectionForm = async () => {
    setIsGenerating(true);
    try {
      const res = await fetchWithAuth(`/pick_up_points/${id}/generate_collection_form`, {
        method: 'POST',
        body: JSON.stringify({ ready_for_collection_only: statusFilter === 'ready_for_collection' }),
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.collection_form_url, '_blank');
        toast.success('Collection form generated successfully');
        setPickUpPoint(prev => prev ? {
          ...prev,
          last_collection_form_generated_at: new Date().toISOString(),
          last_collection_form_url: data.collection_form_url,
        } : prev);
      } else {
        const data = await res.json();
        toast.error(data.errors?.[0] || 'Failed to generate collection form');
      }
    } catch (err) {
      console.error('Failed to generate collection form', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const updateBulkExclusion = async (order: PickUpPointOrder, excluded: boolean, reason?: string) => {
    setSavingExclusionOrderId(order.id);
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/update_bulk_exclusion`, {
        method: 'PATCH',
        body: JSON.stringify({ excluded_from_bulk_update: excluded, bulk_update_exclusion_reason: reason }),
      });
      if (res.ok) {
        toast.success(
          excluded
            ? `${order.order_reference} is excluded from the bulk update`
            : `${order.order_reference} is back in the bulk update`
        );
        setExcludeModalOrder(null);
        setIncludeConfirmOrder(null);
        fetchData(currentPage);
      } else {
        const data = await res.json();
        toast.error(data.errors?.[0] || 'Failed to update the bulk update exclusion');
      }
    } catch (err) {
      console.error('Failed to update the bulk update exclusion', err);
      toast.error('Failed to update the bulk update exclusion');
    } finally {
      setSavingExclusionOrderId(null);
    }
  };

  const handleBulkReadyForCollection = async () => {
    setIsBulkUpdating(true);
    try {
      const res = await fetchWithAuth(`/pick_up_points/${id}/bulk_ready_for_collection`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        const skipped = data.skipped_excluded ?? 0;
        toast.success(
          skipped > 0
            ? `Queued ${data.count} order${data.count !== 1 ? 's' : ''}. ${skipped} excluded order${skipped !== 1 ? 's were' : ' was'} skipped.`
            : 'Bulk ready for collection status update job has been queued'
        );
        fetchData(currentPage);
      } else {
        const data = await res.json();
        toast.error(data.errors?.[0] || 'Failed to bulk update orders');
      }
    } catch (err) {
      console.error('Failed to bulk update orders', err);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  if (isLoading || !pickUpPoint) {
    return <FullscreenLoader message="Loading Pick-Up Point..." />;
  }

  const sendableCount = Math.max(totalCount - excludedCount, 0);

  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">{pickUpPoint.name}</h1>
        {isAdmin && (
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            Edit Pick-Up Point
          </button>
        )}
      </div>

      <div className="bg-white shadow rounded-md p-4 space-y-2 text-sm text-gray-700">
        <p><strong>Code:</strong> {pickUpPoint.code}</p>
        <p><strong>City:</strong> {pickUpPoint.city || 'N/A'}</p>
        <p>
          <strong>Collection Fee:</strong>{' '}
          {pickUpPoint.collection_fee ? `E${Number(pickUpPoint.collection_fee).toFixed(2)}` : 'N/A'}
        </p>
        <p>
          <strong>Default:</strong>{' '}
          <span className={`px-2 py-1 rounded ${pickUpPoint.default ? 'bg-green-200' : 'bg-red-200'}`}>
            {pickUpPoint.default ? 'YES' : 'NO'}
          </span>
        </p>
        <p>
          <strong>Status:</strong>{' '}
          <span className={`px-2 py-1 rounded ${pickUpPoint.active ? 'bg-green-200' : 'bg-gray-200'}`}>
            {pickUpPoint.active ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </p>
        <p>
          <strong>Opening Time:</strong> {pickUpPoint.opening_time || 'N/A'}
        </p>
        <p>
          <strong>Closing Time:</strong> {pickUpPoint.closing_time || 'N/A'}
        </p>
        <p>
          <strong>Collection Disclaimer:</strong> {pickUpPoint.collection_disclaimer || 'N/A'}
        </p>
        <p>
          <strong>Descriptor:</strong> {pickUpPoint.descriptor || 'N/A'}
        </p>
        <p>
          <strong>Slack Channel ID:</strong> {pickUpPoint.slack_channel_id || 'N/A'}
        </p>
        <div>
          <strong>Photo:</strong>{' '}
          {pickUpPoint.photo_url ? (
            <img
              src={pickUpPoint.photo_url}
              alt={`${pickUpPoint.name} collection point`}
              className="mt-2 aspect-[1.91] w-full max-w-md object-cover rounded border"
            />
          ) : (
            'N/A'
          )}
        </div>
        <p>
          <strong>Google Maps Link:</strong>{' '}
          {pickUpPoint.google_maps_url ? (
            <a
              href={pickUpPoint.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline break-all"
            >
              {pickUpPoint.google_maps_url}
            </a>
          ) : (
            'N/A'
          )}
        </p>
        <p>
          <strong>Verified Numbers:</strong>{' '}
          {pickUpPoint.verified_numbers.length > 0
            ? pickUpPoint.verified_numbers.map(vn => vn.phone_number).join(', ')
            : 'None'}
        </p>
      </div>

      {compartmentsSummary && (
        <div className="bg-white shadow rounded-md p-4 text-sm text-gray-700">
          <h3 className="text-lg font-semibold mb-2">Shelf Compartments</h3>
          <div className="flex gap-4">
            <span>Total: <strong>{compartmentsSummary.total}</strong></span>
            <span>Occupied: <strong>{compartmentsSummary.occupied}</strong></span>
            <span>Available: <strong>{compartmentsSummary.available}</strong></span>
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3">
          <h3 className="text-lg font-semibold">Pre-Ready Orders</h3>
          <div className="flex items-start gap-4">
            <select
              className="px-3 py-2 border rounded text-sm"
              value={statusFilter}
              onChange={e => {
                setSearchParams(prev => { prev.set('page', '1'); return prev; });
                setStatusFilter(e.target.value as 'all' | 'ready_for_collection' | 'not_ready_for_collection');
              }}
            >
              <option value="all">All statuses</option>
              <option value="ready_for_collection">Ready for collection only</option>
              <option value="not_ready_for_collection">Not ready for collection only</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap py-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={overstayingOnly}
                onChange={e => {
                  setSearchParams(prev => { prev.set('page', '1'); return prev; });
                  setOverstayingOnly(e.target.checked);
                }}
              />
              {overstayDays === null ? 'Waiting too long' : `Waiting over ${overstayDays} days`}
            </label>
            <div className="flex flex-col">
              <button
                onClick={() => pickUpPoint.last_collection_form_url && window.open(pickUpPoint.last_collection_form_url, '_blank')}
                disabled={!pickUpPoint.last_collection_form_url}
                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
              >
                View Most Recent Form
              </button>
              <span className="text-xs text-gray-400 mt-1">
                Last generated: {pickUpPoint.last_collection_form_generated_at
                  ? new Date(pickUpPoint.last_collection_form_generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                  : 'N/A'}
              </span>
            </div>
            <div className="flex flex-col">
              <button
                onClick={() => setShowGenerateConfirm(true)}
                disabled={isGenerating || orders.length === 0}
                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
              >
                Generate Collection Form
              </button>
            </div>
            <div className="flex flex-col">
              <button
                onClick={() => setShowBulkConfirm(true)}
                disabled={isBulkUpdating || statusFilter !== 'not_ready_for_collection' || orders.length === 0 || sendableCount === 0}
                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
              >
                Bulk Ready for Collection
              </button>
              {statusFilter === 'not_ready_for_collection' && excludedCount > 0 && (
                <span className="text-xs text-gray-400 mt-1">
                  {excludedCount} order{excludedCount !== 1 ? 's' : ''} excluded
                </span>
              )}
            </div>
          </div>
        </div>
        {orders.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left bg-white shadow rounded-md">
                <thead className="bg-gray-50 text-gray-600 text-sm">
                  <tr>
                    <th className="px-4 py-2">Shelf</th>
                    <th className="px-4 py-2">Waiting</th>
                    <th className="px-4 py-2">Order Reference</th>
                    <th className="px-4 py-2">Customer</th>
                    <th className="px-4 py-2">Pre-Ready</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Exclude from Bulk Update</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id} className="border-t border-gray-100 text-sm text-gray-800 hover:bg-yellow-100">
                      <td className="px-4 py-2">
                        {order.shelf_compartment_code || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-2">
                        {order.days_waiting_for_collection == null ? (
                          <span className="text-gray-300">-</span>
                        ) : order.overstaying_at_pick_up_point ? (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            {order.days_waiting_for_collection} days
                          </span>
                        ) : (
                          <span>{order.days_waiting_for_collection} days</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <a href={`/orders/${order.id}`} className="text-blue-600 underline hover:text-blue-800">
                          {order.order_reference}
                        </a>
                      </td>
                      <td className="px-4 py-2">
                        {order.customer ? (
                          <a href={`/customers/${order.customer_id}`} className="text-blue-600 underline hover:text-blue-800">
                            {order.customer.full_name}
                          </a>
                        ) : (
                          <span className="text-gray-500">Unknown customer</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${order.pre_ready_for_collection ? 'bg-green-200' : 'bg-gray-200'}`}>
                          {order.pre_ready_for_collection ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${statusColorMap[order.status]?.class || 'bg-gray-200'}`}>
                          {statusColorMap[order.status]?.text || order.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {order.status === 'ready_for_collection' ? (
                          <span className="text-xs text-gray-400 px-2 whitespace-nowrap">Already sent</span>
                        ) : (
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 mt-0.5 shrink-0"
                              aria-label={`Exclude ${order.order_reference} from bulk update`}
                              checked={order.excluded_from_bulk_update}
                              disabled={savingExclusionOrderId === order.id}
                              onChange={e => {
                                if (e.target.checked) {
                                  setExcludeModalOrder(order);
                                } else {
                                  setIncludeConfirmOrder(order);
                                }
                              }}
                            />
                            {order.excluded_from_bulk_update && order.bulk_update_exclusion_reason && (
                              <div className="text-xs text-gray-500 break-words">
                                <div>{order.bulk_update_exclusion_reason}</div>
                                {(order.bulk_update_excluded_by || order.bulk_update_excluded_at) && (
                                  <div className="text-gray-400">
                                    {[
                                      order.bulk_update_excluded_by && `by ${order.bulk_update_excluded_by}`,
                                      order.bulk_update_excluded_at && formatExclusionDate(order.bulk_update_excluded_at),
                                    ].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-center items-center space-x-4 mt-4">
              <button
                disabled={currentPage === 1}
                onClick={() => {
                  const newPage = currentPage - 1;
                  setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
                  fetchData(newPage);
                }}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => {
                  const newPage = currentPage + 1;
                  setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
                  fetchData(newPage);
                }}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            {statusFilter === 'ready_for_collection'
              ? 'No orders ready for collection at this pick-up point.'
              : statusFilter === 'not_ready_for_collection'
              ? 'No orders pending ready for collection at this pick-up point.'
              : 'No pre-ready orders at this pick-up point.'}
          </p>
        )}
      </div>

      <AddPickUpPointModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          setIsEditModalOpen(false);
          fetchData(currentPage);
        }}
        selectedPickUpPoint={pickUpPoint}
      />
      <ConfirmationModal
        isOpen={showGenerateConfirm}
        onConfirm={() => {
          setShowGenerateConfirm(false);
          handleGenerateCollectionForm();
        }}
        onCancel={() => setShowGenerateConfirm(false)}
        message={
          <span>
            Are you sure you want to generate a collection form for:
            <br /><br />
            <strong>Pick-up point:</strong> {pickUpPoint.name}
            <br />
            <strong>Ready for collection only:</strong> {statusFilter === 'ready_for_collection' ? 'True' : 'False'}
          </span>
        }
      />
      <ConfirmationModal
        isOpen={showBulkConfirm}
        onConfirm={() => {
          setShowBulkConfirm(false);
          handleBulkReadyForCollection();
        }}
        onCancel={() => setShowBulkConfirm(false)}
        message={
          <span>
            Are you sure you want to update <strong>{sendableCount}</strong> order{sendableCount !== 1 ? 's' : ''} to ready for collection?
            <br /><br />
            A WhatsApp notification will be sent to each customer.
            {excludedCount > 0 && (
              <>
                <br /><br />
                <strong>{excludedCount}</strong> excluded order{excludedCount !== 1 ? 's' : ''} will be skipped.
              </>
            )}
          </span>
        }
      />
      <BulkExclusionModal
        isOpen={excludeModalOrder !== null}
        orderReference={excludeModalOrder?.order_reference ?? ''}
        isSaving={savingExclusionOrderId !== null}
        onConfirm={reason => {
          if (excludeModalOrder) updateBulkExclusion(excludeModalOrder, true, reason);
        }}
        onCancel={() => setExcludeModalOrder(null)}
      />
      <ConfirmationModal
        isOpen={includeConfirmOrder !== null}
        onConfirm={() => {
          if (includeConfirmOrder) updateBulkExclusion(includeConfirmOrder, false);
        }}
        onCancel={() => setIncludeConfirmOrder(null)}
        message={
          <span>
            Put <strong>{includeConfirmOrder?.order_reference}</strong> back into the bulk update?
            <br /><br />
            It will be marked ready for collection — and the customer notified — the next time you run
            Bulk Ready for Collection.
            {includeConfirmOrder?.bulk_update_exclusion_reason && (
              <>
                <br /><br />
                <strong>Excluded because:</strong> {includeConfirmOrder.bulk_update_exclusion_reason}
              </>
            )}
          </span>
        }
      />
      {isGenerating && <FullscreenLoader message="Generating Collection Form..." />}
    </div>
  );
};

export default PickUpPointDetails;
